import { Route } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { initialDataResolver } from './app.resolvers';
import { AuthGuard } from './core/guards/auth.guard';
import { NoAuthGuard } from './core/guards/noAuth.guard';
import { Authority } from './config/authority.constants';
import { UserRouteAccessService } from './core/auth/user-route-access.service';

export const appRoutes: Route[] = [
	// Redirect empty path to '/example'
	{ path: '', pathMatch: 'full', redirectTo: 'main' },

	// Redirect signed-in user to the '/example'
	//
	// After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
	// path. Below is another redirection for that path to redirect the user to the desired
	// location. This is a small convenience to keep all main routes together here on this file.
	{ path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'main' },

	// Auth routes for guests
	{
		path: '',
		canActivate: [NoAuthGuard],
		canActivateChild: [NoAuthGuard],
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
		children: [
			{
				path: 'confirmation-required',
				loadChildren: () =>
					import('app/modules/auth/confirmation-required/confirmation-required.routes'),
			},
			{
				path: 'forgot-password',
				loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
			},
			{
				path: 'reset-password',
				loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
			},
			{ path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
			{ path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
		],
	},

	// Auth routes for authenticated users
	{
		path: '',
		canActivate: [AuthGuard],
		canActivateChild: [AuthGuard],
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
		children: [
			{ path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
			{
				path: 'unlock-session',
				loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes'),
			},
		],
	},

	// Landing routes
	{
		path: '',
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
		children: [
			{ path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
		],
	},

	// Admin routes without authorities
	{
		path: '',
		canActivate: [AuthGuard],
		canActivateChild: [AuthGuard],
		component: LayoutComponent,
		resolve: {
			initialData: initialDataResolver,
		},
		children: [
			{
				path: '',
				loadChildren: () =>
					import('app/modules/not-found/not-found.module').then((m) => m.NotFoundModule),
			},
			{
				path: '',
				loadChildren: () =>
					import('app/modules/access-denied/access-denied.module').then(
						(m) => m.AccessDeniedModule
					),
			},
			{
				path: 'main',
				loadChildren: () => import('app/modules/admin/main/main.module').then((m) => m.MainModule),
			},
			{
				path: 'example',
				loadChildren: () =>
					import('app/modules/admin/example/example.module').then((m) => m.ExampleModule),
			},
		],
	},

	// Admin routes with authorities [ADMIN]
	{
		path: 'admin',
		canActivate: [AuthGuard, UserRouteAccessService],
		canActivateChild: [AuthGuard],
		component: LayoutComponent,
		resolve: {
			initialData: initialDataResolver,
		},
		data: {
			authorities: [Authority.ADMIN, Authority.USER],
		},
		loadChildren: () =>
			import('./modules/admin/admin-routing.module').then((m) => m.AdminRoutingModule),
	},
	{
		path: 'account',
		canActivate: [AuthGuard, UserRouteAccessService],
		canActivateChild: [AuthGuard],
		component: LayoutComponent,
		resolve: {
			initialData: initialDataResolver,
		},
		data: {
			authorities: [Authority.USER],
		},
		loadChildren: () => import('./modules/account/account.module').then((m) => m.AccountModule),
	},
	{
		path: 'modules',
		canActivate: [AuthGuard, UserRouteAccessService],
		canActivateChild: [AuthGuard],
		component: LayoutComponent,
		data: {
			authorities: [Authority.USER],
		},
		resolve: {
			initialData: initialDataResolver,
		},
		loadChildren: () => import(`./modules/modules.module`).then((m) => m.ModulesModule),
	},
	{
		path: '**',
		pathMatch: 'full',
		component: LayoutComponent,
		data: {
			layout: 'empty',
			state: 'notFound',
		},
		resolve: {
			initialData: initialDataResolver,
		},
	},
];
